/**
 * groupClientFiles — TASK-818.
 *
 * The Files tab rendered every artefact in the account as two flat lists: one table of
 * working documents and one run of deliverables. With nine "brief deck" rows carrying no
 * client and no distinguishing text, the list said almost nothing — the only way to find a
 * client's documents was to read every row, or to type their name into a filter box.
 *
 * This splits the same data by client, which is how the work is actually organised. Kept
 * pure and out of the component so the grouping and filtering rules can be tested directly.
 *
 * Deliverables and working documents stay separate WITHIN each client group. That is
 * TASK-741's rule and it still holds: an export is a rendered artefact of a document at a
 * version, not another document, and merging the two produced counts nobody could explain.
 */

import type { WorkbenchDocumentSummary } from '../services/api/documentApi';
import type { ExportListItem } from '../services/api/exportApi';

/** Bucket for artefacts with no client_id — a real state, not an error. */
export const NO_CLIENT = '__none__';

export const ALL = 'all';

export type FileKind = typeof ALL | 'documents' | 'deliverables';

export interface FileFilters {
  /** ALL | client id | NO_CLIENT */
  client: string;
  /** ALL | 'docx' | 'xlsx' | 'pptx' | 'pdf' */
  format: string;
  kind: FileKind;
  /** Secondary free-text match; the dropdowns are the primary filter. */
  search: string;
}

export const EMPTY_FILTERS: FileFilters = {
  client: ALL,
  format: ALL,
  kind: ALL,
  search: '',
};

export interface ClientFileGroup {
  clientId: string;
  clientName: string;
  documents: WorkbenchDocumentSummary[];
  deliverables: ExportListItem[];
  total: number;
  /** Newest timestamp in the group, for ordering. 0 when nothing is dated. */
  lastActivityMs: number;
}

export interface ClientOption {
  id: string;
  name: string;
  count: number;
}

const time = (iso: string | null | undefined): number => {
  if (!iso) return 0;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? 0 : ms;
};

/** Deliverables carry a client_id but never a name; working documents carry both. */
export const clientKey = (id: string | null | undefined): string => id || NO_CLIENT;

export const clientLabel = (
  id: string,
  names: Map<string, string>
): string => {
  if (id === NO_CLIENT) return 'No client assigned';
  // A name we cannot resolve is shown as the id rather than invented or hidden — the
  // document is real and has to stay reachable.
  return names.get(id) || id;
};

/** Format of an export; the server leaves it off for older brief/portfolio rows. */
export const exportFormat = (e: ExportListItem): string => (e.format || 'pptx').toLowerCase();

/**
 * Build the client dropdown's options from the artefacts that actually exist, so the list
 * never offers a client with nothing behind it. Ordered by count, unassigned always last.
 */
export function clientOptions(
  documents: WorkbenchDocumentSummary[],
  deliverables: ExportListItem[],
  names: Map<string, string>
): ClientOption[] {
  const counts = new Map<string, number>();
  for (const d of documents) {
    const k = clientKey(d.client_id);
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  for (const e of deliverables) {
    const k = clientKey(e.client_id);
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }

  return [...counts.entries()]
    .map(([id, count]) => ({ id, name: clientLabel(id, names), count }))
    .sort((a, b) => {
      if (a.id === NO_CLIENT) return 1;
      if (b.id === NO_CLIENT) return -1;
      return a.name.localeCompare(b.name);
    });
}

/** Formats present in the data, so the Type dropdown offers only real choices. */
export function formatOptions(
  documents: WorkbenchDocumentSummary[],
  deliverables: ExportListItem[]
): string[] {
  const formats = new Set<string>();
  documents.forEach((d) => formats.add(d.format.toLowerCase()));
  deliverables.forEach((e) => formats.add(exportFormat(e)));
  return [...formats].sort();
}

const matches = (haystack: string, needle: string) =>
  haystack.toLowerCase().includes(needle);

export function groupClientFiles(params: {
  documents: WorkbenchDocumentSummary[];
  deliverables: ExportListItem[];
  names: Map<string, string>;
  /** Supplied by the caller because the label resolves an export's source document title. */
  deliverableLabel: (e: ExportListItem) => string;
  filters: FileFilters;
}): ClientFileGroup[] {
  const { documents, deliverables, names, deliverableLabel, filters } = params;
  const search = filters.search.trim().toLowerCase();

  const keepClient = (id: string) => filters.client === ALL || filters.client === id;

  const groups = new Map<string, ClientFileGroup>();
  const ensure = (id: string): ClientFileGroup => {
    let group = groups.get(id);
    if (!group) {
      group = {
        clientId: id,
        clientName: clientLabel(id, names),
        documents: [],
        deliverables: [],
        total: 0,
        lastActivityMs: 0,
      };
      groups.set(id, group);
    }
    return group;
  };

  if (filters.kind !== 'deliverables') {
    for (const d of documents) {
      const id = clientKey(d.client_id);
      if (!keepClient(id)) continue;
      if (filters.format !== ALL && d.format.toLowerCase() !== filters.format) continue;
      if (
        search &&
        !matches(d.title ?? '', search) &&
        !matches(d.client_name ?? clientLabel(id, names), search) &&
        !matches(d.format, search)
      ) {
        continue;
      }
      const group = ensure(id);
      group.documents.push(d);
      group.total += 1;
      group.lastActivityMs = Math.max(group.lastActivityMs, time(d.updated_at));
    }
  }

  if (filters.kind !== 'documents') {
    for (const e of deliverables) {
      const id = clientKey(e.client_id);
      if (!keepClient(id)) continue;
      if (filters.format !== ALL && exportFormat(e) !== filters.format) continue;
      if (
        search &&
        !matches(deliverableLabel(e), search) &&
        !matches(clientLabel(id, names), search)
      ) {
        continue;
      }
      const group = ensure(id);
      group.deliverables.push(e);
      group.total += 1;
      group.lastActivityMs = Math.max(
        group.lastActivityMs,
        time(e.completed_at) || time(e.created_at)
      );
    }
  }

  for (const group of groups.values()) {
    group.documents.sort((a, b) => time(b.updated_at) - time(a.updated_at));
    group.deliverables.sort(
      (a, b) =>
        (time(b.completed_at) || time(b.created_at)) -
        (time(a.completed_at) || time(a.created_at))
    );
  }

  // Most recently touched client first — that is the one being worked on. Unassigned
  // artefacts sink to the bottom regardless: they are a loose end, not a client.
  return [...groups.values()].sort((a, b) => {
    if (a.clientId === NO_CLIENT) return 1;
    if (b.clientId === NO_CLIENT) return -1;
    return b.lastActivityMs - a.lastActivityMs;
  });
}
